import type { RequestListItem } from '../../types/styling-analysis.js';

/** @graph-connects none */
interface RequestListTableProps {
  items: RequestListItem[];
  totalCount: number;
  limit: number;
  offset: number;
  onPageChange: (newOffset: number) => void;
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business Display paginated table of raw customer request records with styling request text, scenes, and reject reason.
 * @graph-connects none
 */
export function RequestListTable({
  items,
  totalCount,
  limit,
  offset,
  onPageChange,
}: RequestListTableProps) {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(totalCount / limit);

  if (items.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
        データがありません
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs text-[var(--color-text-muted)]">
          {String(totalCount)}件中 {String(offset + 1)}-
          {String(Math.min(offset + limit, totalCount))}
          件を表示
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => {
              onPageChange(Math.max(0, offset - limit));
            }}
            className="rounded-md border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] disabled:opacity-40"
          >
            前へ
          </button>
          <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
            {String(currentPage)} / {String(totalPages)}
          </span>
          <button
            type="button"
            disabled={offset + limit >= totalCount}
            onClick={() => {
              onPageChange(offset + limit);
            }}
            className="rounded-md border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] disabled:opacity-40"
          >
            次へ
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                日付
              </th>
              <th className="px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                オーダーID
              </th>
              <th className="px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                ステータス
              </th>
              <th className="px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                着用シーン
              </th>
              <th className="min-w-[300px] px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                スタイリングリクエスト
              </th>
              <th className="min-w-[200px] px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                再選定理由
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={`${item.orderId}-${item.orderedDate}`}
                className="border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-elevated)]"
              >
                <td className="px-3 py-2 font-mono text-xs text-[var(--color-text-primary)]">
                  {item.orderedDate}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-[var(--color-text-muted)]">
                  {item.orderId}
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[var(--color-text-secondary)]">
                    {item.orderStatusLabel || `ステータス ${item.orderStatus}`}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {item.scenes.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {item.scenes.map((scene) => (
                        <span
                          key={scene}
                          className="rounded-full bg-[var(--color-accent-primary)]/10 px-2 py-0.5 text-[var(--color-accent-primary)]"
                        >
                          {scene}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">-</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs leading-relaxed text-[var(--color-text-primary)]">
                  {item.stylingRequest || <span className="text-[var(--color-text-muted)]">-</span>}
                </td>
                <td className="px-3 py-2 text-xs leading-relaxed text-[var(--color-text-primary)]">
                  {item.rejectReason || <span className="text-[var(--color-text-muted)]">-</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
